import {
  ArrowPathIcon,
  MinusIcon,
  PlusIcon
} from "@heroicons/react/24/outline";

import { processBody } from "./helper";
import { uiConfigService } from "src/services/apis";
import { notify } from "../../../../../src/shared/context/AlertContext";
import { FinBoxResponse } from "../../../../../src/shared/services/APIResponse";
import {
  IUiConfigButtonAPIActionParams,
  IUiConfigButtonClientSideInput,
  IUiConfigButtonFunctionActionParams
} from "./types";
import {
  Delete,
  Get,
  Post,
  Put
} from "../../../../../src/shared/services/Fetch";

const UiConfigButtonActions = {
  getIconComponent: (icon: string) => {
    switch (icon) {
      case "plus":
        return <PlusIcon className="w-4 h-4 mr-2" />;
      case "minus":
        return <MinusIcon className="w-4 h-4 mr-2" />;
      case "refresh":
        return <ArrowPathIcon className="w-8 h-8 mr-2" />;
    }
  },

  handleApiCall: async (
    params: IUiConfigButtonAPIActionParams,
    clientSideInput: IUiConfigButtonClientSideInput,
    setIsLoading: (isLoading: boolean) => void,
    notifySuccess: boolean = true
  ) => {
    try {
      setIsLoading(true);

      const { endpoint, method, body } = params;
      const processedBody = processBody(body, clientSideInput);

      let response: FinBoxResponse<unknown> & { statusCode: number };
      switch (method) {
        case "POST":
          response = await Post(endpoint, clientSideInput.token, processedBody);
          break;
        case "GET":
          response = await Get(endpoint, clientSideInput.token);
          break;
        case "PUT":
          response = await Put(endpoint, clientSideInput.token, processedBody);
          break;
        case "DELETE":
          response = await Delete(endpoint, clientSideInput.token);
          break;
        default:
          response = {
            data: null,
            error: "Unsupported HTTP method",
            status: false,
            statusCode: 500
          };

          break;
      }
      // Early return pattern: handle invalid response first
      if (!response || typeof response !== "object" || !("status" in response))
        return;

      // Early return pattern: handle success case first
      if (response.status) {
        if (notifySuccess) {
          notify({
            title: "Success",
            text:
              (response.data as { message?: string })?.message ||
              "Action completed successfully",
            type: "success"
          });
        }

        return response;
      }

      // Handle error case
      notify({
        title: "Error",
        text: response.error || "Action failed",
        type: "error"
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error calling API:", error);
      notify({
        title: "Error",
        text: message,
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  },

  handleFunctionCall: async (
    params: IUiConfigButtonFunctionActionParams,
    clientSideInput: IUiConfigButtonClientSideInput,
    sectionID: string,
    tabName: string,
    isLenderDashboard: boolean,
    setIsLoading: (isLoading: boolean) => void
  ) => {
    setIsLoading(true);
    try {
      const { token, loanApplicationID, userID, ...dynamicFields } =
        clientSideInput;

      const response = await uiConfigService.executeUIConfig(
        token,
        {
          loanApplicationID,
          userID,
          sectionName: sectionID,
          resourceName: tabName,
          data: {
            ...dynamicFields,
            ...(params.args ?? {})
          }
        },
        isLenderDashboard
      );

      if (response.status) {
        notify({
          title: "Success",
          text:
            (response.data as { message?: string })?.message ||
            "Action completed successfully",
          type: "success"
        });

        return response;
      }

      notify({
        title: "Error",
        text: response.error || "Action failed",
        type: "error"
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);

      console.error("Error calling API:", error);
      notify({ title: "Error", text: message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  }
};

export const { handleApiCall, getIconComponent } = UiConfigButtonActions;

export default UiConfigButtonActions;
